import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'framer-motion';
import { queryClient } from '@/api/queryClient';
import { endpoints } from '@/api';
import { useAuth } from '@/stores/auth';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { AppShell } from '@/components/layout/AppShell';
import { PublicOnly, RequireAuth, RequireNotOnboarded, RequireOnboarded, RequireRole } from '@/components/layout/guards';
import { ConnectionBanner } from '@/components/layout/ConnectionBanner';
import { InviteCenter, ModerationGate, RealtimeBridge, TimezoneSync } from '@/components/layout/Global';
import { FaceGateHost } from '@/features/face/FaceGate';
import { AdsHost } from '@/features/ads/AdsHost';
import { DropWaitlistNotifier } from '@/features/drops/waitlist';
import { GuidelinesReacceptHost } from '@/features/onboarding/GuidelinesReaccept';
import { FinishProfileHost, RequireCompleteProfile } from '@/features/onboarding/FinishProfile';
import { Toaster } from '@/components/ui/Toaster';
import { FullScreenLoader } from '@/components/ui/States';
import { Landing } from '@/features/landing/Landing';
import { Login } from '@/features/auth/Login';
import { NotFound } from '@/features/misc/NotFound';
import { Home } from '@/features/home/Home';
import { InviteComposerHost } from '@/features/invites/InviteSheets';
import { AfterDateHost } from '@/features/after/AfterDateHost';
import { NotifyHost } from '@/features/notify/NotifyHost';
import { GuestSwitcher } from '@/features/guest/GuestSwitcher';
import { OutsideUs, UsModeGate } from '@/features/us/mode';
import { UsRingHost } from '@/features/us/UsRing';

const Onboarding = lazy(() => import('@/features/onboarding/Onboarding'));
const Match = lazy(() => import('@/features/match/Match'));
const CallPage = lazy(() => import('@/features/call/CallPage'));
const Friends = lazy(() => import('@/features/friends/Friends'));
const RequestsPage = lazy(() => import('@/features/requests/RequestsPage').then((m) => ({ default: m.RequestsPage })));
const Chats = lazy(() => import('@/features/chat/Chats'));
const Plus = lazy(() => import('@/features/plus/Plus'));
const Drops = lazy(() => import('@/features/drops/Drops'));
const DropDetail = lazy(() => import('@/features/drops/DropDetail'));
const PodView = lazy(() => import('@/features/drops/PodView'));
const Settings = lazy(() => import('@/features/settings/Settings'));
const Learned = lazy(() => import('@/features/insights/Learned'));
const Safety = lazy(() => import('@/features/safety/Safety'));
const ModLayout = lazy(() => import('@/features/mod/ModLayout'));
const ModReports = lazy(() => import('@/features/mod/ModReports'));
const ModMetrics = lazy(() => import('@/features/mod/ModMetrics'));
const ModTraffic = lazy(() => import('@/features/mod/ModTraffic'));
const ModUsers = lazy(() => import('@/features/mod/ModUsers'));
const ModDates = lazy(() => import('@/features/mod/ModDates'));
const ModWaitlist = lazy(() => import('@/features/mod/ModWaitlist'));
const ModDrops = lazy(() => import('@/features/mod/ModDrops'));
const ModDetections = lazy(() => import('@/features/mod/ModDetections'));
const ModSettings = lazy(() => import('@/features/mod/ModSettings'));
const InvitePage = lazy(() => import('@/features/invites/InvitePage'));
const LobbyPage = lazy(() => import('@/features/invites/LobbyPage'));
const CoinsPage = lazy(() => import('@/features/coins/CoinsPage'));
const GiftShelf = lazy(() => import('@/features/gifts/Shelf'));
// v9 C: My dates (the private journal), one date, dates with one person.
const JournalPage = lazy(() => import('@/features/after/JournalPage'));
const DatePage = lazy(() => import('@/features/after/DatePage'));
const PersonPage = lazy(() => import('@/features/after/PersonPage'));
// v9 area A: personal links, express onboarding's "Finish your profile".
const LinkSettings = lazy(() => import('@/features/links/LinkSettings'));
const DeleteAccountPage = lazy(() => import('@/features/settings/DeleteAccount'));
const PublicLinkPage = lazy(() => import('@/features/links/PublicLinkPage'));
// /try: plan a date as a guest, sign in only to send it (docs/SPEC_TRY.md).
const TryPage = lazy(() => import('@/features/try/TryPage'));
const GuestHome = lazy(() => import('@/features/guest/GuestHome'));
// v15 Us (docs/SPEC_V15_US.md): the private couple space and its own shell.
const UsShell = lazy(() => import('@/features/us/UsShell'));
const UsHome = lazy(() => import('@/features/us/UsHome'));
const UsJoin = lazy(() => import('@/features/us/UsJoin'));
const UsChat = lazy(() => import('@/features/us/UsChat'));
const UsCall = lazy(() => import('@/features/us/UsCall'));
const WatchParty = lazy(() => import('@/features/us/watch/WatchParty'));
const UsSettings = lazy(() => import('@/features/us/UsSettings'));
const ThemeStudio = lazy(() => import('@/features/us/ThemeStudio'));
const FinishProfilePage = lazy(() => import('@/features/onboarding/Onboarding').then((m) => ({ default: m.FinishProfilePage })));

/** On boot, trade the httpOnly refresh cookie for an in-memory access token. */
function Boot() {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    endpoints.refresh().catch(() => useAuth.getState().clear());
  }, []);
  return null;
}

const authed = (el: JSX.Element) => (
  <RequireAuth>
    <RequireOnboarded>
      {/* v15: in Us mode the app is the couple's space until they leave the mode. */}
      <UsModeGate>{el}</UsModeGate>
    </RequireOnboarded>
  </RequireAuth>
);

/** v9 §A1: `/@handle` is a personal link page (works logged out); any other single segment is a 404. */
function HandleRoute() {
  const { atHandle = '' } = useParams();
  if (!atHandle.startsWith('@') || atHandle.length < 2) return <NotFound />;
  return <PublicLinkPage handle={atHandle.slice(1)} />;
}

/** Old /people links keep working. */
function PeopleRedirect() {
  const { '*': rest } = useParams();
  return <Navigate to={`/friends${rest ? `/${rest}` : ''}`} replace />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <Routes>
        <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/safety" element={<Safety />} />
        {/* Works logged out and before onboarding (sign-in alone sends the link). */}
        <Route path="/try" element={<TryPage />} />
        <Route path="/guest" element={<RequireAuth><GuestHome /></RequireAuth>} />
        {/* v6.1: invite links work logged out; the lobby needs an account. */}
        <Route path="/d/:token" element={<InvitePage />} />
        <Route path="/:atHandle" element={<HandleRoute />} />
        <Route path="/profile/finish" element={authed(<FinishProfilePage />)} />
        <Route path="/dates/:inviteId" element={authed(<LobbyPage />)} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <RequireNotOnboarded>
                <Onboarding />
              </RequireNotOnboarded>
            </RequireAuth>
          }
        />
        <Route path="/match" element={authed(<RequireCompleteProfile><Match /></RequireCompleteProfile>)} />
        <Route path="/call/:id" element={authed(<CallPage />)} />
        <Route path="/drops/:id/pod/:podId" element={authed(<PodView />)} />
        <Route path="/us/join/:code" element={authed(<UsJoin />)} />
        <Route path="/us" element={authed(<UsShell />)}>
          <Route index element={<UsHome />} />
          <Route path="chat" element={<UsChat />} />
          <Route path="call" element={<UsCall />} />
          <Route path="watch" element={<WatchParty />} />
          <Route path="settings" element={<UsSettings />} />
          <Route path="theme" element={<ThemeStudio />} />
        </Route>
        <Route element={authed(<AppShell />)}>
          <Route path="/home" element={<Home />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/friends" element={<Friends />} />
          <Route path="/friends/:connectionId/chat" element={<Chats />} />
          <Route path="/chats" element={<Chats />} />
          <Route path="/people/*" element={<PeopleRedirect />} />
          <Route path="/plus" element={<Plus />} />
          <Route path="/drops" element={<Drops />} />
          <Route path="/drops/:id" element={<DropDetail />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/learned" element={<Learned />} />
          <Route path="/settings/link" element={<LinkSettings />} />
          <Route path="/settings/delete" element={<DeleteAccountPage />} />
          {/* v8 coins & gifts (both redirect away while the gifts flag is off). */}
          <Route path="/coins" element={<CoinsPage />} />
          <Route path="/settings/gifts" element={<GiftShelf />} />
          <Route path="/dates" element={<JournalPage />} />
          <Route path="/dates/s/:sessionId" element={<DatePage />} />
          <Route path="/dates/with/:userId" element={<PersonPage />} />
          <Route
            path="/mod"
            element={
              <RequireRole roles={['moderator', 'admin']}>
                <ModLayout />
              </RequireRole>
            }
          >
            <Route index element={<ModReports />} />
            <Route path="detections" element={<ModDetections />} />
            <Route path="metrics" element={<RequireRole roles={['admin']}><ModMetrics /></RequireRole>} />
            <Route path="traffic" element={<RequireRole roles={['admin']}><ModTraffic /></RequireRole>} />
            <Route path="users" element={<RequireRole roles={['admin']}><ModUsers /></RequireRole>} />
            <Route path="dates" element={<RequireRole roles={['admin']}><ModDates /></RequireRole>} />
            <Route path="waitlist" element={<RequireRole roles={['admin']}><ModWaitlist /></RequireRole>} />
            <Route path="drops" element={<RequireRole roles={['admin']}><ModDrops /></RequireRole>} />
            <Route path="settings" element={<RequireRole roles={['admin']}><ModSettings /></RequireRole>} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <Boot />
            <RealtimeBridge />
            <TimezoneSync />
            <ConnectionBanner />
            <AppRoutes />
            {/* v15: the rest of Popin stays quiet inside a couple's space (safety never does). */}
            <OutsideUs>
              <InviteCenter />
              <InviteComposerHost />
              <AfterDateHost />
              <FinishProfileHost />
              <AdsHost />
              <DropWaitlistNotifier />
              <NotifyHost />
              <GuestSwitcher />
            </OutsideUs>
            <FaceGateHost />
            <GuidelinesReacceptHost />
            <ModerationGate />
            <UsRingHost />
            <Toaster />
          </BrowserRouter>
        </MotionConfig>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
